import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    AdminCreateWalletTransactionRequestSchema,
    AdminCreateWalletTransactionResponseSchema,
    AdminDeleteWalletTransactionRequestSchema,
    AdminDeletedWalletTransactionSchema,
    AdminPayoutRequestSchema,
    AdminPayoutApprovalPreviewSchema,
    AdminDecidePayoutRequestRequestSchema,
    AdminReleaseWalletHoldRequestSchema,
    ExpertWalletLevelProgressSchema,
    AdminReconcileAllWalletAllocationsSummarySchema,
    AdminReconcileWalletAllocationsSummarySchema,
    AdminWalletResponseSchema,
    AdminWalletTransactionSchema,
    AdminWalletStatisticsOverviewSchema,
    AdminExpertWalletBalanceSchema,
    AdminExpertWalletBalancesMetaSchema,
    type AdminPayoutRequestsQueryParams,
    type AdminWalletTransactionsQueryParams,
    type AdminWalletStatisticsQueryParams,
    type AdminExpertWalletBalancesQueryParams,
} from "../schemas/wallet";

export function createAdminWalletApi(http: HttpClient, baseUrl: string) {
    const adminOrganizationsBaseUrl = `${baseUrl}/admin/organizations`;
    const adminWalletTransactionsBaseUrl = `${baseUrl}/admin/wallet-transactions`;
    const adminPayoutRequestsBaseUrl = `${baseUrl}/admin/payout-requests`;
    const adminWalletStatisticsBaseUrl = `${baseUrl}/admin/wallet-statistics`;

    return {
        getOrganizationWallet: createEndpoint<
            z.infer<typeof AdminWalletResponseSchema>,
            unknown,
            QueryParams,
            { organization: number }
        >(http, "GET", "/:organization/wallet", {
            base: adminOrganizationsBaseUrl,
            responseSchema: AdminWalletResponseSchema,
            params: ["organization"],
        }),

        getOrganizationWalletLevel: createEndpoint<
            z.infer<typeof ExpertWalletLevelProgressSchema>,
            unknown,
            QueryParams,
            { organization: number }
        >(http, "GET", "/:organization/wallet/level", {
            base: adminOrganizationsBaseUrl,
            responseSchema: ExpertWalletLevelProgressSchema,
            params: ["organization"],
        }),

        listOrganizationTransactions: createEndpointWithPagination<
            z.infer<typeof AdminWalletTransactionSchema>,
            unknown,
            unknown,
            AdminWalletTransactionsQueryParams,
            { organization: number }
        >(http, "GET", "/:organization/wallet/transactions", {
            base: adminOrganizationsBaseUrl,
            itemSchema: AdminWalletTransactionSchema,
            params: ["organization"],
        }),

        listOrganizationHoldTransactions: createEndpoint<
            z.infer<typeof AdminWalletTransactionSchema>[],
            unknown,
            QueryParams,
            { organization: number }
        >(http, "GET", "/:organization/wallet/hold-transactions", {
            base: adminOrganizationsBaseUrl,
            responseSchema: z.array(AdminWalletTransactionSchema),
            params: ["organization"],
        }),

        createOrganizationTransaction: createEndpoint<
            z.infer<typeof AdminCreateWalletTransactionResponseSchema>,
            z.input<typeof AdminCreateWalletTransactionRequestSchema>,
            QueryParams,
            { organization: number }
        >(http, "POST", "/:organization/wallet/transactions", {
            base: adminOrganizationsBaseUrl,
            responseSchema: AdminCreateWalletTransactionResponseSchema,
            params: ["organization"],
        }),

        reconcileOrganizationAllocations: createEndpoint<
            z.infer<typeof AdminReconcileWalletAllocationsSummarySchema>,
            unknown,
            QueryParams,
            { organization: number }
        >(http, "POST", "/:organization/wallet/reconcile-allocations", {
            base: adminOrganizationsBaseUrl,
            responseSchema: AdminReconcileWalletAllocationsSummarySchema,
            params: ["organization"],
        }),

        reconcileAllAllocations: createEndpoint<
            z.infer<typeof AdminReconcileAllWalletAllocationsSummarySchema>
        >(http, "POST", "/reconcile", {
            base: `${baseUrl}/admin/wallet-allocations`,
            responseSchema: AdminReconcileAllWalletAllocationsSummarySchema,
        }),

        listWalletTransactions: createEndpointWithPagination<
            z.infer<typeof AdminWalletTransactionSchema>,
            unknown,
            unknown,
            AdminWalletTransactionsQueryParams
        >(http, "GET", "", {
            base: adminWalletTransactionsBaseUrl,
            itemSchema: AdminWalletTransactionSchema,
        }),

        releaseHold: createEndpoint<
            z.infer<typeof AdminWalletTransactionSchema>,
            z.input<typeof AdminReleaseWalletHoldRequestSchema>,
            QueryParams,
            { transaction: number }
        >(http, "POST", "/:transaction/release-hold", {
            base: adminWalletTransactionsBaseUrl,
            responseSchema: AdminWalletTransactionSchema,
            params: ["transaction"],
        }),

        deleteTransaction: createEndpoint<
            z.infer<typeof AdminDeletedWalletTransactionSchema>,
            z.input<typeof AdminDeleteWalletTransactionRequestSchema>,
            QueryParams,
            { transaction: number }
        >(http, "DELETE", "/:transaction", {
            base: adminWalletTransactionsBaseUrl,
            responseSchema: AdminDeletedWalletTransactionSchema,
            params: ["transaction"],
        }),

        getWalletStatistics: createEndpoint<
            z.infer<typeof AdminWalletStatisticsOverviewSchema>,
            unknown,
            AdminWalletStatisticsQueryParams
        >(http, "GET", "", {
            base: adminWalletStatisticsBaseUrl,
            responseSchema: AdminWalletStatisticsOverviewSchema,
        }),

        listExpertWalletBalances: createEndpointWithPagination<
            z.infer<typeof AdminExpertWalletBalanceSchema>,
            z.infer<typeof AdminExpertWalletBalancesMetaSchema>,
            unknown,
            AdminExpertWalletBalancesQueryParams
        >(http, "GET", "/experts", {
            base: adminWalletStatisticsBaseUrl,
            itemSchema: AdminExpertWalletBalanceSchema,
            metaSchema: AdminExpertWalletBalancesMetaSchema,
        }),

        listPayoutRequests: createEndpointWithPagination<
            z.infer<typeof AdminPayoutRequestSchema>,
            unknown,
            unknown,
            AdminPayoutRequestsQueryParams
        >(http, "GET", "", {
            base: adminPayoutRequestsBaseUrl,
            itemSchema: AdminPayoutRequestSchema,
        }),

        previewPayoutRequestApproval: createEndpoint<
            z.infer<typeof AdminPayoutApprovalPreviewSchema>,
            unknown,
            QueryParams,
            { payoutRequest: number }
        >(http, "GET", "/:payoutRequest/approval-preview", {
            base: adminPayoutRequestsBaseUrl,
            responseSchema: AdminPayoutApprovalPreviewSchema,
            params: ["payoutRequest"],
        }),

        approvePayoutRequest: createEndpoint<
            z.infer<typeof AdminPayoutRequestSchema>,
            z.input<typeof AdminDecidePayoutRequestRequestSchema>,
            QueryParams,
            { payoutRequest: number }
        >(http, "POST", "/:payoutRequest/approve", {
            base: adminPayoutRequestsBaseUrl,
            responseSchema: AdminPayoutRequestSchema,
            params: ["payoutRequest"],
        }),

        rejectPayoutRequest: createEndpoint<
            z.infer<typeof AdminPayoutRequestSchema>,
            z.input<typeof AdminDecidePayoutRequestRequestSchema>,
            QueryParams,
            { payoutRequest: number }
        >(http, "POST", "/:payoutRequest/reject", {
            base: adminPayoutRequestsBaseUrl,
            responseSchema: AdminPayoutRequestSchema,
            params: ["payoutRequest"],
        }),
    };
}

export type AdminWalletApi = ReturnType<typeof createAdminWalletApi>;
